import React, { useState } from 'react';
import { Heart, Shield, CheckCircle } from 'lucide-react';
import { usePaystackPayment } from 'react-paystack';
import NewsletterSection from '../components/NewsletterSection';

const PRESET_AMOUNTS = [
  { amount: 50},
  { amount: 150},
  { amount: 500},
  { amount: 1000},
];

const generateReference = () => `ROLF-${Date.now()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

const DonateForm = () => {
  const [donateAmount, setDonateAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [reference, setReference] = useState(generateReference());

  const effectiveAmount = donateAmount || Number(customAmount) || 0;

  const config = {
    reference,
    email: email || 'placeholder@rolf.org', // Paystack requires email at hook init
    amount: effectiveAmount * 100, // Convert ZAR to cents
    publicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
    currency: 'ZAR',
    metadata: {
      custom_fields: [
        {
          display_name: 'Donor Name',
          variable_name: 'donor_name',
          value: name,
        },
      ],
    },
  };

  const initializePayment = usePaystackPayment(config);

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Please enter your name.';
    if (!email.trim()) newErrors.email = 'Please enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Please enter a valid email.';
    if (!effectiveAmount || effectiveAmount < 10) newErrors.amount = 'Minimum donation is R10.';
    return newErrors;
  };

  const onSuccess = () => {
    setSuccess(true);
  };

  const onClose = () => {
    // Generate a fresh reference for the next attempt
    setReference(generateReference());
  };

  const handleDonate = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    initializePayment(onSuccess, onClose);
  };

  const handlePresetSelect = (amount) => {
    if (donateAmount === amount) {
      setDonateAmount(null); // deselect → show custom input
    } else {
      setDonateAmount(amount);
      setCustomAmount('');
      setErrors((prev) => ({ ...prev, amount: undefined }));
    }
  };

  const handleCustomAmount = (e) => {
    setCustomAmount(e.target.value);
    setDonateAmount(null);
    setErrors((prev) => ({ ...prev, amount: undefined }));
  };

  if (success) {
    return (
      <div className="scroll-animate card" style={{ padding: '3rem', textAlign: 'center' }}>
        <CheckCircle size={56} style={{ color: 'var(--teal)', marginBottom: '1rem' }} />
        <h3 style={{ marginBottom: '0.5rem' }}>Thank You, {name}!</h3>
        <p style={{ color: 'var(--gray-500)', marginBottom: '2rem' }}>
          Your donation of <strong>R{effectiveAmount}</strong> has been received. A receipt will be sent to <strong>{email}</strong>.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => {
            setSuccess(false);
            setDonateAmount(null);
            setCustomAmount('');
            setName('');
            setEmail('');
            setReference(generateReference());
          }}
        >
          Make Another Donation
        </button>
      </div>
    );
  }

  return (
    <div className="scroll-animate card" style={{ padding: '3rem' }}>
      <h3 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>Choose a Donation Amount</h3>
      <p style={{ textAlign: 'center', marginBottom: '2rem', fontSize: '0.9rem' }}>
        Select a preset amount, or click "Other" to enter a custom amount.
      </p>

      {/* Preset amounts + Other */}
      <div className="donate-amount-grid" style={{ marginBottom: donateAmount === null ? '1rem' : '2rem' }}>
        {PRESET_AMOUNTS.map((opt) => (
          <button
            key={opt.amount}
            className={`donate-amount-btn ${donateAmount === opt.amount ? 'selected' : ''}`}
            onClick={() => handlePresetSelect(opt.amount)}
            style={{
              background: donateAmount === opt.amount ? 'rgba(78,185,191,0.1)' : 'var(--gray-50)',
              border: `2px solid ${donateAmount === opt.amount ? 'var(--teal)' : 'var(--gray-200)'}`,
            }}
          >
            <div className="donate-amount" style={{ color: 'var(--navy)' }}>R{opt.amount}</div>
            {/* <div className="donate-desc" style={{ color: 'var(--gray-500)' }}>{opt.desc}</div> */}
          </button>
        ))}
        <button
          className={`donate-amount-btn ${donateAmount === null ? 'selected' : ''}`}
          onClick={() => { setDonateAmount(null); setCustomAmount(''); setErrors((prev) => ({ ...prev, amount: undefined })); }}
          style={{
            background: donateAmount === null ? 'rgba(78,185,191,0.1)' : 'var(--gray-50)',
            border: `2px solid ${donateAmount === null ? 'var(--teal)' : 'var(--gray-200)'}`,
          }}
        >
          <div className="donate-amount" style={{ color: 'var(--navy)' }}>Other</div>
          {/* <div className="donate-desc" style={{ color: 'var(--gray-500)' }}>Custom amount</div> */}
        </button>
      </div>

      {/* Custom amount — only shown when no preset is selected */}
      {donateAmount === null && (
        <div className="form-group">
          <label className="form-label">Custom Amount (ZAR)</label>
          <input
            className="form-input"
            type="number"
            min="10"
            placeholder="Enter amount"
            value={customAmount}
            onChange={handleCustomAmount}
            autoFocus
          />
          {errors.amount && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '0.25rem' }}>{errors.amount}</p>}
        </div>
      )}
      {donateAmount !== null && errors.amount && (
        <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginBottom: '1rem' }}>{errors.amount}</p>
      )}

      {/* Donor details */}
      <div className="donate-form-row" style={{ marginBottom: '2rem' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Full Name</label>
          <input
            className="form-input"
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => { setName(e.target.value); setErrors((prev) => ({ ...prev, name: undefined })); }}
          />
          {errors.name && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '0.25rem' }}>{errors.name}</p>}
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Email</label>
          <input
            className="form-input"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErrors((prev) => ({ ...prev, email: undefined })); }}
          />
          {errors.email && <p style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '0.25rem' }}>{errors.email}</p>}
        </div>
      </div>

      <button className="btn btn-primary btn-lg" style={{ width: '100%' }} onClick={handleDonate}>
        <Heart size={18} /> Donate {effectiveAmount > 0 ? `R${effectiveAmount}` : ''} via Paystack
      </button>

      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <p style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>
          <Shield size={14} style={{ display: 'inline', marginRight: '0.25rem', verticalAlign: 'middle' }} />
          Secure payment via Paystack. ROLF is a registered NPO (2021/766891/08). Donations are tax-deductible under Section 18A.
        </p>
      </div>
    </div>
  );
};

const DonatePage = () => (
  <>
    <section className="donate-section" style={{ paddingTop: '120px', paddingBottom: '4rem' }}>
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div className="section-header" style={{ marginBottom: '2rem' }}>
          <span className="section-tag" style={{ color: 'var(--teal-light)' }}>Support Our Mission</span>
          <h1 style={{ color: '#fff' }}>Make a Donation</h1>
          <p style={{ color: 'rgba(255,255,255,0.7)' }}>
            Your generosity fuels our programmes and transforms lives. Every rand makes a difference.
          </p>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container" style={{ maxWidth: '800px' }}>
        <DonateForm />
      </div>
    </section>

    <NewsletterSection />
  </>
);

export default DonatePage;
